/**
 * analytics-queue.ts — first-party analytics batch-queue STUB (STORY-003-003).
 *
 * EPIC-009 ships the real drainer; this module only provides the
 * client-side enqueue side of the contract so the marketplace deep-link
 * helpers (`trackContactClick` in `@/lib/deep-links`) have somewhere to
 * push click events today. Until EPIC-009 wires a drainer, events simply
 * accumulate on `window.__gardenAnalyticsQueue` and are never sent.
 *
 * Hard guardrails encoded here (do NOT relax in EPIC-009 without an ADR):
 *   - FIRST-PARTY ONLY. No third-party tracker scripts (GA / Plausible /
 *     Mixpanel / Segment / …) — forbidden per REQ-PRD-§11.2-C and the
 *     forbidden-libraries charter in 05-GUARDRAILS.md. This stub posts
 *     nowhere; the future drainer posts to GardEn's own endpoint only.
 *   - NO PII IN PAYLOADS. E.164 phone numbers, emails, addresses and KG
 *     ID numbers must never enter an analytics event (REQ-TRD-§14.6-A).
 *     `AnalyticsEvent.props` is deliberately typed to opaque scalar IDs
 *     (seller/product UUIDs + a channel enum) — never a raw contact value.
 *   - NO localStorage. The queue is an in-memory `window` array only; it
 *     is intentionally NOT persisted (REQ-PRD-§11.2-A spirit + no-tracker
 *     rule). It is also NOT a service worker (no PWA — PRD §7).
 *
 * The queue is a plain array so EPIC-009 can drain it with `.splice(0)`
 * without coupling to this module. `enqueueAnalyticsEvent` is a no-op when
 * `window` is undefined (SSR / non-DOM test contexts) so importing this in
 * a pure helper never throws.
 */

/** Contact channels a marketplace surface can deep-link out to. */
export type AnalyticsContactChannel = "whatsapp" | "telegram" | "phone" | "2gis";

/**
 * The (small, PII-free) property bag carried on an analytics event.
 *
 * Values are opaque identifiers and enums only — never a raw phone
 * number, email, address or any other free-text PII. EPIC-009 may widen
 * the union, but the "no PII" invariant is permanent.
 */
export interface AnalyticsEventProps {
  channel?: AnalyticsContactChannel;
  sellerId?: string;
  productId?: string;
}

/** A single queued first-party analytics event. */
export interface AnalyticsEvent {
  /** Dot-namespaced event name, e.g. `marketplace.contact_click`. */
  name: string;
  /** Epoch milliseconds the event was enqueued (client clock). */
  ts: number;
  /** PII-free property bag. */
  props: AnalyticsEventProps;
}

declare global {
  interface Window {
    /**
     * In-memory, first-party analytics batch queue. EPIC-009 attaches a
     * drainer that flushes to GardEn's own collector endpoint. Absent
     * until something enqueues (see `enqueueAnalyticsEvent`).
     */
    __gardenAnalyticsQueue?: AnalyticsEvent[];
  }
}

/** The window global key, exported so the EPIC-009 drainer can reuse it. */
export const ANALYTICS_QUEUE_KEY = "__gardenAnalyticsQueue" as const;

/**
 * Push a first-party analytics event onto the in-memory batch queue.
 *
 * Lazily creates `window.__gardenAnalyticsQueue` on first use. No-op when
 * there is no `window` (e.g. a pure-function unit test in a non-DOM
 * environment) so callers never have to guard the DOM themselves.
 *
 * IMPORTANT: never pass PII (phone numbers, emails, addresses) in
 * `props`. The type system steers callers toward opaque IDs; this is the
 * last line of defence, not the only one.
 */
export function enqueueAnalyticsEvent(name: string, props: AnalyticsEventProps): void {
  if (typeof window === "undefined") {
    return;
  }
  const queue: AnalyticsEvent[] = window.__gardenAnalyticsQueue ?? [];
  queue.push({ name, ts: Date.now(), props });
  window.__gardenAnalyticsQueue = queue;
}
